import type { Album, AlbumFullObject } from '../interfaces/albums';
import type { Artist } from '../interfaces/artist';
import type { Track } from '../interfaces/track';
import { backendClient } from '../lib/api/backendClient';

type BackendEntity<T> = {
  id: string;
  data?: T;
  [key: string]: unknown;
};

const unwrap = <T>(entity?: BackendEntity<T> | null): T | null => {
  if (!entity) return null;
  const payload = entity.data;
  if (payload && typeof payload === 'object') {
    return payload as T;
  }
  return (entity ?? null) as T | null;
};

const ensureAlbumWithTracks = (album: Album | AlbumFullObject | null, tracks: Track[]): AlbumFullObject | null => {
  if (!album) return null;
  const existingTracks =
    (album as AlbumFullObject)?.tracks && Array.isArray((album as AlbumFullObject).tracks)
      ? ((album as AlbumFullObject).tracks as unknown as Track[])
      : [];
  return {
    ...(album as Album),
    tracks: existingTracks.length ? existingTracks : tracks
  };
};

const safeExtractTracks = (entries: Array<BackendEntity<Track> | Track | null | undefined>): Track[] => {
  return entries
    .map((entry) => {
      if (!entry) return null;
      if ('data' in entry && typeof entry.data === 'object') {
        return entry.data as Track;
      }
      return entry as Track;
    })
    .filter((track): track is Track => Boolean(track));
};

export const spotifyCacheService = {
  async fetchAlbumWithTracks(spotifyAlbumId: string): Promise<{ album: AlbumFullObject | null; tracks: Track[] }> {
    try {
      const { data } = await backendClient.get<{
        album?: BackendEntity<Album | AlbumFullObject>;
        tracks?: BackendEntity<Track>[];
      }>(`/cache/spotify/albums/${encodeURIComponent(spotifyAlbumId)}?withTracks=true`);

      const album = unwrap<Album | AlbumFullObject>(data?.album);
      const tracks = safeExtractTracks(data?.tracks ?? []);
      const normalized = ensureAlbumWithTracks(album, tracks);
      return {
        album: normalized,
        tracks: normalized?.tracks ?? tracks
      };
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return { album: null, tracks: [] };
      }
      throw error;
    }
  },

  async fetchArtist(spotifyArtistId: string): Promise<Artist | null> {
    try {
      const { data } = await backendClient.get<{ artist?: BackendEntity<Artist> }>(
        `/cache/spotify/artists/${encodeURIComponent(spotifyArtistId)}`
      );
      return unwrap<Artist>(data?.artist);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  async fetchAlbumsForArtist(spotifyArtistId: string): Promise<Album[]> {
    try {
      const { data } = await backendClient.get<{ albums?: BackendEntity<Album>[] }>(
        `/cache/spotify/artists/${encodeURIComponent(spotifyArtistId)}/albums`
      );
      const albums = (data?.albums ?? [])
        .map((album) => unwrap<Album>(album))
        .filter((entry): entry is Album => Boolean(entry));
      return albums;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  async fetchArtistDiscography(spotifyArtistId: string): Promise<{
    spotifyArtistId: string;
    albums: Album[];
    totalAlbums?: number;
    fetchedAt?: string;
  } | null> {
    try {
      const { data } = await backendClient.get<{
        discography?: {
          spotifyArtistId?: string;
          albums?: Album[];
          totalAlbums?: number;
          fetchedAt?: string;
        };
      }>(`/cache/spotify/artists/${encodeURIComponent(spotifyArtistId)}/discography`);

      const discography = data?.discography;
      if (!discography || !Array.isArray(discography.albums)) {
        return null;
      }

      return {
        spotifyArtistId: discography.spotifyArtistId ?? spotifyArtistId,
        albums: discography.albums.filter(Boolean) as Album[],
        totalAlbums: typeof discography.totalAlbums === 'number' ? discography.totalAlbums : undefined,
        fetchedAt: discography.fetchedAt,
      };
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }
};

export default spotifyCacheService;
